import { FormField, TextInput } from "@a2z/ui";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  WALLET_PROVIDERS,
  type WalletProviderValue,
} from "@/modules/onboarding/constants/wallet-providers";
import { WalletLogo } from "./payment-logos";
import { ReceiverEchoLine } from "./ReceiverEchoLine";
import { PHONE_RE, type WalletRow } from "./payment-form";

type WalletFieldsProps = {
  wallets: WalletRow[];
  onToggleProvider: (provider: WalletProviderValue) => void;
  onPhoneChange: (provider: WalletProviderValue, phone: string) => void;
};

export function WalletFields({
  wallets,
  onToggleProvider,
  onPhoneChange,
}: WalletFieldsProps) {
  const t = useTranslations();
  const locale = useLocale();

  return (
    <div className="flex flex-col gap-4">
      {/* Provider picker — recognizable brand marks, greyscale until selected. */}
      <div className="flex flex-wrap gap-2">
        {WALLET_PROVIDERS.map((p) => {
          const selected = wallets.some((w) => w.provider === p.value);
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => onToggleProvider(p.value)}
              className={[
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
                selected
                  ? "border-[color:var(--accent-500)] bg-[color:var(--surface-alt)] text-[color:var(--accent-600)]"
                  : "border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)]",
              ].join(" ")}
            >
              <WalletLogo provider={p.value} active={selected} size={18} />
              {locale === "ar" ? p.labelAr : p.labelEn}
            </button>
          );
        })}
      </div>

      {/* One phone field per selected wallet, with a buyer-facing echo line.
          Fields flow into a 2-column grid so widths stay consistent and pair
          up on wider screens. */}
      {wallets.length > 0 ? (
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          {wallets.map((w) => {
            const p = WALLET_PROVIDERS.find((x) => x.value === w.provider);
            const label = `${locale === "ar" ? p?.labelAr : p?.labelEn} · ${t(keys.seller.pmWalletNumber)}`;
            const invalid =
              w.phone.trim().length > 0 && !PHONE_RE.test(w.phone.trim());
            return (
              <div key={w.provider} className="flex flex-col gap-1.5">
                <FormField label={label} name={`wallet-${w.provider}`}>
                  <TextInput
                    name={`wallet-${w.provider}`}
                    inputMode="numeric"
                    dir="ltr"
                    hasError={invalid}
                    placeholder={`${p?.prefix ?? "01"}xxxxxxxx`}
                    value={w.phone}
                    onChange={(e) => onPhoneChange(w.provider, e.target.value)}
                  />
                </FormField>
                {w.prefilled ? (
                  <p className="text-xs text-[color:var(--text-muted)]">
                    {t(keys.seller.pmWalletPrefillHint)}
                  </p>
                ) : (
                  <ReceiverEchoLine value={w.phone} />
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
